import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { When } from 'react-if';
import { head } from 'lodash';
import { theme } from 'antd';

import { useStores } from '@/hooks/use-stores';
import { useMeasurement } from '@/hooks/useMeasurement';
import { useValueByConnectivity } from '@/hooks/useValueByConnectivity';
import { AlertsWidget } from '@/comp/MapBuilder/components/AlertsWidget';
import { Ports } from '@/comp/MapBuilder/components/Ports';
import AirTankWidget from '@/comp/common/icons/AirTankWidget';
import AirTankMaintenanceWidget from '@/comp/common/icons/AirTankMaintenanceWidget';

import { Container } from './AirTankNodeWidget.styles';
import { isSelectedNodeOnMap, getWidgetFillColor } from '../../utils/utils';
import { NodeModelTypeEnum } from '../enums';
import { DUMMY_MODEL_IDS } from '../nodeWidget/EditAssetDrawer/EditAssetDrawer.utils';
import { getBorderInfo } from '../compressor/CompressorNodeWidget.utils';

const { useToken } = theme;

export const AirTankNodeWidget = observer(({ node, engine = null, width, height, type }) => {
  const { t } = useTranslation();
  const { token } = useToken();
  const { tvalue, tunit } = useMeasurement();
  const { tcvalue, tcunit } = useValueByConnectivity();
  const { capacity, connectivity, deviceId, inMaintenance, deviceModelId } = node.device;
  const firstPressureSensor: {
    pressureBar: number;
  } = head(node.onDevices.filter(device => device?.deviceType === 'pressureSensor'));

  const { mapBuilderStore } = useStores();
  const renderedOnMap = !!engine;
  const isSelected = isSelectedNodeOnMap(deviceId, mapBuilderStore.selectedDevice?.deviceId, renderedOnMap);
  const backgroundColor = getWidgetFillColor(isSelected, token, renderedOnMap);
  const isDry = type === NodeModelTypeEnum.DryAirTank;
  const hasNoMatchingSpec =
    deviceModelId === (isDry ? DUMMY_MODEL_IDS.dryAirTank : DUMMY_MODEL_IDS.wetAirTank) && renderedOnMap;
  const borderInfo = getBorderInfo(isSelected, inMaintenance, false, hasNoMatchingSpec, token);

  return (
    <Container width={width} height={height} $mapView={engine ? -1 : 1} $textColor={borderInfo?.textColor}>
      {borderInfo ? (
        <AirTankMaintenanceWidget
          width={width}
          height={height}
          fillColor={borderInfo.fillColor}
          borderColor={borderInfo.borderColor}
          dry={isDry}
          className="background"
        />
      ) : (
        <AirTankWidget width={width} height={height} color={backgroundColor} dry={isDry} className="background" />
      )}

      <When condition={!!engine}>
        {borderInfo && <div className="title">{t(borderInfo.headerText)}</div>}

        <header>
          <span>{t(isDry ? 'Dry tank' : 'Wet tank')}</span>
        </header>
        <section>
          <div className="capacity-container">
            <span className="value">{tcvalue(tvalue(capacity, 'liter'), connectivity)}</span>
            <span className="label">{tcunit(tunit('Liter'), capacity, connectivity)}</span>
          </div>
          {firstPressureSensor && firstPressureSensor?.pressureBar != null && (
            <div className="pressure-container">
              <span className="value">{tcvalue(tvalue(firstPressureSensor?.pressureBar, 'bar'), connectivity)}</span>
              <span className="label">{tcunit(tunit('bar'), firstPressureSensor?.pressureBar, connectivity)}</span>
            </div>
          )}
          <AlertsWidget alerts={node.alerts} />
        </section>

        <Ports node={node} engine={engine} width={width} height={height} />
      </When>
    </Container>
  );
});
